import React, { useEffect } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  notification,
  Row,
  Select,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { IInbound } from '@/interfaces/inbound';
import { updateInbound } from '@/adapters/inbound';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { getInboundStatusOptions } from '@/helpers/inbouldHelper';

interface IProps {
  dataModal?: IDataModal<Partial<IInbound>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const InboundEditModal = ({ dataModal, handleOk, handleCancel }: IProps) => {
  const [form] = Form.useForm<Partial<IInbound>>();
  const mutations = useMutation({
    mutationFn: async (data: Partial<IInbound>) => updateInbound(data),
    mutationKey: ['update-inbound'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'อัพเดทข้อมูลสำเร็จ',
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IInbound>) => {
        values.id = Number(dataModal?.id || 0);
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.id) {
      form.setFieldsValue({
        ...dataModal?.item,
        whReceivedDate:
          (dataModal?.item?.whReceivedDate &&
            dayjs(dataModal?.item?.whReceivedDate)) ||
          undefined,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  const _businessUnitId = Form.useWatch('businessUnitId', form);

  return (
    <Modal
      title={'อัพเดทข้อมูล'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="productGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="inbound-update"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
        >
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item<IInbound> label="BU" name="businessUnitId">
                <BusinessUnitSelector
                  handleSelectData={id => {
                    form.setFieldsValue({
                      businessUnitId: id,
                    });
                  }}
                  value={_businessUnitId}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IInbound>
                label="วันที่รับเข้าสินค้า"
                name="whReceivedDate"
              >
                <DatePicker size="large" locale={locale} className="w-full" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IInbound>
                label="สถานะ"
                name="status"
                rules={[{ required: true, message: 'เลือกสถานะ' }]}
              >
                <Select size="large" options={getInboundStatusOptions()} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default InboundEditModal;
